import React, { useState, useEffect, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { 
  BarChart3, Printer, MapPin, AlertTriangle, Clock, CheckCircle, 
  XCircle, TrendingUp, RefreshCw, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: Format milliseconds to readable time
const formatDuration = (ms) => {
  if (!ms || ms === 0) return '0s';
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Helper: Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

// Helper: Get default date range (last 7 days)
const getDefaultDateRange = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  return {
    from: sevenDaysAgo.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0]
  };
};

export default function KitchenAnalyticsPage() {
  // Date range state
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [datePreset, setDatePreset] = useState('7days');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [stations, setStations] = useState([]);
  const [failures, setFailures] = useState({ topReasons: [], recentFailures: [] });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all analytics data
  const fetchAnalytics = useLatestCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    const params = `from=${dateRange.from}&to=${dateRange.to}`;
    
    try {
      const [summaryRes, printersRes, stationsRes, failuresRes] = await Promise.all([
        fetch(`${API_URL}/api/kitchen/analytics/summary?${params}`, {
          headers: getBranchAuthHeaders()
        }),
        fetch(`${API_URL}/api/kitchen/analytics/printers?${params}`, {
          headers: getBranchAuthHeaders()
        }),
        fetch(`${API_URL}/api/kitchen/analytics/stations?${params}`, {
          headers: getBranchAuthHeaders()
        }),
        fetch(`${API_URL}/api/kitchen/analytics/failures?${params}`, {
          headers: getBranchAuthHeaders()
        })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (printersRes.ok) setPrinters((await printersRes.json()).rows || []);
      if (stationsRes.ok) setStations((await stationsRes.json()).rows || []);
      if (failuresRes.ok) setFailures(await failuresRes.json());
      
    } catch (err) {
      toast.error('Failed to load analytics data');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  });

  // Initial load
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Handle date preset change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    let from = new Date();
    
    switch (preset) {
      case '7days':
        from.setDate(today.getDate() - 7);
        break;
      case '30days':
        from.setDate(today.getDate() - 30);
        break;
      case '90days':
        from.setDate(today.getDate() - 90);
        break;
      case 'today':
        from = today;
        break;
      case 'custom':
        return; // Don't change dates for custom
      default:
        from.setDate(today.getDate() - 7);
    }
    
    setDateRange({
      from: from.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    });
  };

  // Apply date filter
  const applyDateFilter = () => {
    fetchAnalytics(true);
  };

  // Peak hour calculation
  const peakHour = useMemo(() => {
    if (!summary?.busiestHours?.length) return null;
    const max = summary.busiestHours.reduce((a, b) => a.tickets > b.tickets ? a : b);
    return max.tickets > 0 ? max : null;
  }, [summary]);

  // KPI Cards data
  const kpiCards = [
    {
      title: 'Total Tickets',
      value: summary?.totals?.tickets || 0,
      icon: Printer,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Success Rate',
      value: `${summary?.totals?.successRate || 0}%`,
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      subtitle: `${summary?.totals?.printed || 0} printed`
    },
    {
      title: 'Avg Print Time',
      value: formatDuration(summary?.totals?.avgPrintTimeMs),
      icon: Clock,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      title: 'Failed Jobs',
      value: summary?.totals?.failed || 0,
      icon: XCircle,
      color: summary?.totals?.failed > 0 ? 'text-red-500' : 'text-slate-400',
      bgColor: summary?.totals?.failed > 0 ? 'bg-red-500/10' : 'bg-slate-500/10'
    }
  ];

  if (loading) {
    return (
      <div data-testid="kitchen-analytics-page" className="p-6">
        <div className="flex items-center justify-center h-64">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
          <span className="ml-2 text-muted-foreground">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="kitchen-analytics-page" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Kitchen Analytics
          </h1>
          <p className="text-muted-foreground">
            Print job performance and metrics
          </p>
        </div>
        
        {/* Date Range Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={datePreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          
          {datePreset === 'custom' && (
            <>
              <Input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="w-[140px]"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="w-[140px]"
              />
            </>
          )}
          
          <Button onClick={applyDateFilter} disabled={refreshing}>
            {refreshing ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Calendar className="w-4 h-4 mr-1" />
            )}
            Apply
          </Button>
        </div>
      </div>

      {/* Date Range Badge */}
      <div className="flex items-center gap-2">
        <Badge variant="outline">
          {summary?.range?.from} → {summary?.range?.to}
        </Badge>
        {peakHour && (
          <Badge variant="secondary">
            <TrendingUp className="w-3 h-3 mr-1" />
            Peak: {peakHour.hour}:00 ({peakHour.tickets} tickets)
          </Badge>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map((kpi, idx) => (
          <Card key={idx} data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className="text-3xl font-bold mt-1">{kpi.value}</p>
                  {kpi.subtitle && (
                    <p className="text-sm text-muted-foreground mt-1">{kpi.subtitle}</p>
                  )}
                </div>
                <div className={`p-3 rounded-full ${kpi.bgColor}`}>
                  <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Busiest Hours Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" /> Busiest Hours
          </CardTitle>
          <CardDescription>Print jobs by hour of day</CardDescription>
        </CardHeader>
        <CardContent>
          {summary?.busiestHours?.some(h => h.tickets > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary.busiestHours}>
                <XAxis 
                  dataKey="hour" 
                  tickFormatter={(h) => `${h}:00`}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip 
                  formatter={(value) => [value, 'Tickets']}
                  labelFormatter={(hour) => `${hour}:00 - ${hour}:59`}
                />
                <Bar dataKey="tickets" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {summary.busiestHours.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.hour === peakHour?.hour ? '#10b981' : '#3b82f6'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              No ticket data for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two Column Layout: Printers & Stations */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Printer Utilization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" /> Printer Utilization
            </CardTitle>
            <CardDescription>Jobs per printer</CardDescription>
          </CardHeader>
          <CardContent>
            {printers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Printer</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Success</TableHead>
                    <TableHead className="text-right">Avg Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {printers.slice(0, 8).map((printer) => (
                    <TableRow key={printer.printerId}>
                      <TableCell className="font-medium">
                        {printer.printerName.length > 20 
                          ? printer.printerName.slice(0, 20) + '...' 
                          : printer.printerName}
                      </TableCell>
                      <TableCell className="text-right">{printer.tickets}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={printer.successRate >= 90 ? 'default' : printer.successRate >= 70 ? 'secondary' : 'destructive'}>
                          {printer.successRate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatDuration(printer.avgPrintTimeMs)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No printer data for selected period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Station Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Station Performance
            </CardTitle>
            <CardDescription>Jobs per station</CardDescription>
          </CardHeader>
          <CardContent>
            {stations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Station</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Success</TableHead>
                    <TableHead className="text-right">Avg Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stations.slice(0, 8).map((station) => (
                    <TableRow key={station.stationCode}>
                      <TableCell className="font-medium">
                        <div>{station.stationName}</div>
                        <div className="text-xs text-muted-foreground">{station.stationCode}</div>
                      </TableCell>
                      <TableCell className="text-right">{station.tickets}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={station.successRate >= 90 ? 'default' : station.successRate >= 70 ? 'secondary' : 'destructive'}>
                          {station.successRate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatDuration(station.avgPrintTimeMs)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No station data for selected period
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Failures Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Failure Analysis
          </CardTitle>
          <CardDescription>Top failure reasons and recent failures</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Top Reasons */}
            <div>
              <h4 className="text-sm font-medium mb-3">Top Failure Reasons</h4>
              {failures.topReasons?.length > 0 ? (
                <div className="space-y-2">
                  {failures.topReasons.slice(0, 5).map((reason, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm font-medium">{reason.reason}</span>
                      <Badge variant="destructive">{reason.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No failures 🎉
                </div>
              )}
            </div>

            {/* Recent Failures Table */}
            <div className="lg:col-span-2">
              <h4 className="text-sm font-medium mb-3">Recent Failures</h4>
              {failures.recentFailures?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Printer</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Message</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {failures.recentFailures.slice(0, 10).map((failure, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs">
                            {failure.createdAt ? new Date(failure.createdAt).toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="text-sm">{failure.printerName}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {failure.reason}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {failure.message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No failures in selected period
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
